import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import GenericSelect from '../../components/ui/GenericSelect';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import useDeepLinkParams from '../../hooks/useDeepLinkParams';
import {
  useManagedCalibrationPeriods, useCalibrationTeamQueue, useCalibrationManagerActions,
  CALIBRATION_STATUS_COLORS, StatusBadge,
} from './config';
import CalibrationDecisionModal from './CalibrationDecisionModal';

// PRD PM §5 (Task 30) — yönetici kalibrasyon el sıkışma ekranı: ekip kuyruğu
// (durum rozetli) → detay modalı (360 özeti + hedef sonuçları + sürekli GB
// referans paneli + nihai seviye onayı). TeamGoalsPage'in (Task 27) sayfa
// desenini izler (period seçici + DataTable + detay modalı).
//
// Gelen Kutum derin bağlantısı (2026-10-28): ?period=&record= ile gelinince
// dönem seçilir ve kuyruk yüklenince o kaydın karar modalı doğrudan açılır
// (kuyruk dönemin tamamını getirir — sayfalama yok). Modal kapanınca
// parametreler adresten silinir; seçili dönem kalır.
const Container = styled.div``;

const PeriodSelectWrap = styled.div`
  min-width: 280px;
  margin-bottom: 16px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  overflow: hidden;
`;

const EmptyBox = styled.div`
  padding: 26px 18px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

// Faz B (spec 2026-08-07 §2.2/§6, Task 6) — satırda "Oturumda: {ad}" rozeti;
// CalibrationTeamQueueItemResponse.SessionName YALNIZ kayıt AKTİF bir
// kalibrasyon oturumuna bağlıysa dolu (bkz. CalibrationDataAccess.
// GetTeamQueueAsync yorumu) — kapanmış/iptal edilmiş oturumlarda satır bire
// bir eski haliyle kalır. StatusBadge'den (durum rozeti) AYRI/nötr bir renk
// (indigo) — ikisi karışmasın diye.
const SessionBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: #EEF2FF;
  color: #4F46E5;
`;

const emptyModal = { open: false, record: null };

export default function CalibrationManagerPage() {
  const { t } = useTranslation();
  // PMF-NEW-01 — kurtarma (zorla onay) YALNIZ yönetimsel aktöre gösterilir;
  // backend aynı kapıyı ForceApproveAsync içinde bağımsız uygular
  // (403 performance.calibration.forceForbidden) — bu yalnız görsel daraltma.
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { isHr } = useAuth();
  const canForce = Boolean(isHr);

  const { periods, periodsFetching } = useManagedCalibrationPeriods();
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const { values: { period: linkedPeriodId, record: linkedRecordId }, clear: clearDeepLink } = useDeepLinkParams(['period', 'record']);

  useEffect(() => {
    if (linkedPeriodId) setSelectedPeriodId(linkedPeriodId);
  }, [linkedPeriodId]);

  useEffect(() => {
    if (!selectedPeriodId && periods.length > 0) setSelectedPeriodId(periods[0].id);
  }, [periods, selectedPeriodId]);

  const periodOptions = useMemo(() => periods.map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.period.status.${p.status}`, { defaultValue: p.status })})`,
  })), [periods, t]);

  const { records, fetching, refresh } = useCalibrationTeamQueue(selectedPeriodId);
  const {
    getManagerView, getFeedbackSummary, getEmployeeGoals, managerApprove, forceApprove,
  } = useCalibrationManagerActions();

  const [modal, setModal] = useState(emptyModal);
  const openDetail = useCallback((row) => setModal({
    open: true,
    record: {
      recordId: row.recordId, employeeId: row.employeeId, employeeName: row.employeeName, periodId: row.periodId, periodName: row.periodName,
    },
  }), []);
  const closeModal = useCallback(() => {
    setModal(emptyModal);
    clearDeepLink();
  }, [clearDeepLink]);
  const handleApproved = useCallback(() => {
    closeModal();
    refresh();
  }, [closeModal, refresh]);

  // Derin bağlantının kaydı kuyrukta görününce modal BİR KEZ açılır (kapatınca yenilenen kuyruk yeniden açmaz).
  const openedLinkRef = useRef(null);
  useEffect(() => {
    if (!linkedRecordId || fetching || openedLinkRef.current === linkedRecordId) return;
    const row = records.find((r) => r.recordId === linkedRecordId);
    if (!row) return;
    openedLinkRef.current = linkedRecordId;
    openDetail(row);
  }, [linkedRecordId, records, fetching, openDetail]);

  const columns = useMemo(() => [
    { name: 'recordId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.goal.fields.employee'),
      options: {
        filter: false,
        sort: false,
        // Faz B (spec 2026-08-07 §2.2/§6, Task 6) — kayıt aktif bir
        // kalibrasyon oturumuna bağlıysa (bkz. SessionBadge yorumu) isim
        // yanında rozet.
        customBodyRender: (v, tableMeta) => {
          const row = records[tableMeta.rowIndex];
          return (
            <>
              {v}
              {row?.sessionName && (
                <SessionBadge>{t('performance.calibration.inSession', { name: row.sessionName })}</SessionBadge>
              )}
            </>
          );
        },
      },
    },
    {
      name: 'status',
      label: t('common.status'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => {
          const cfg = CALIBRATION_STATUS_COLORS[v] || CALIBRATION_STATUS_COLORS.invited;
          return (
            <StatusBadge color={cfg.color} bg={cfg.bg}>
              {t(`performance.calibration.status.${v}`, { defaultValue: v })}
            </StatusBadge>
          );
        },
      },
    },
    {
      // Plan 2 (Task 8) — CalibrationTeamQueueItemResponse.FinalLevelLabel
      // (kaydın kendi seçimi, ANLIK etiket) — yönetici henüz onaylamadıysa
      // FinalLevelId null, kolon '-' gösterir.
      name: 'finalLevelLabel',
      label: t('performance.calibration.selectLevel'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = records[tableMeta.rowIndex];
          if (!row) return null;
          const actions = [{
            type: 'details',
            label: t('performance.teamGoals.actions.viewDetail'),
            onClick: () => openDetail(row),
          }];
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [t, records, openDetail]);

  return (
    <Container>
      <CalibrationDecisionModal
        open={modal.open}
        record={modal.record}
        onClose={closeModal}
        onApproved={handleApproved}
        getManagerView={getManagerView}
        getFeedbackSummary={getFeedbackSummary}
        getEmployeeGoals={getEmployeeGoals}
        managerApprove={managerApprove}
        canForce={canForce}
        forceApprove={forceApprove}
      />

      <PeriodSelectWrap>
        <GenericSelect
          label={t('performance.goal.selectPeriod')}
          value={selectedPeriodId}
          onChange={setSelectedPeriodId}
          options={periodOptions}
          idKey="id"
          labelKey="name"
        />
      </PeriodSelectWrap>

      {!periodsFetching && periods.length === 0 && (
        <Card><EmptyBox>{t('performance.calibration.noPeriods')}</EmptyBox></Card>
      )}

      {periods.length > 0 && !selectedPeriodId && (
        <Card><EmptyBox>{t('performance.goal.selectPeriodFirst')}</EmptyBox></Card>
      )}

      {selectedPeriodId && (
        <DataTable
          title={t('performance.calibration.managerTitle')}
          fetching={fetching}
          columns={columns}
          data={records}
          serverSide={false}
          canCreateNewRecord={false}
        />
      )}
    </Container>
  );
}
